import { Component } from '../Component';
import { HHDSVideoDirect, HHDSVideoDirectAttrNames, HHDSVideoDirectTagName } from '../VideoDirect/VideoDirect';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSImage, HHDSImageEvent, HHDSImageTagName } from '../Image/Image';
import { HHDSVideoEvent } from '../../utils/VideoTypes';
import gsap, { Cubic } from 'gsap';
import { PageEvent } from '../../common/page-lifecycle/PageEvent';
import { HHDSDialogEvent } from '../Dialog/Dialog';

const DEBUG_VERBOSE: boolean = false;
export const HHDSHeroTagName: string = 'hhds-hero';
const TAG_NAME: string = HHDSHeroTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

const SUSPEND_PLAYBACK_FOR_DIALOG: boolean = true;

export enum HHDSHeroMediaType {
	video = 'video',
	image = 'image',
}

export const HHDSHeroAttrNames = {
	type: 'type',
	src: 'src',
	srcSet: 'srcset',
	sizes: 'sizes',
	cover: 'cover',
	overlayEnabled: 'overlay',
	header: 'header',
	subtitle: 'subtitle',
	showScrollButton: 'show-scroll-button',
	parallax: 'parallax',
	alt: 'alt',
	force16x9: 'force-16x9',
};

const Attrs = HHDSHeroAttrNames;

export class HHDSHero extends Component {
	private video: HHDSVideoDirect | null = null;
	private image: HHDSImage | null = null;
	private overlay: HTMLElement | null = null;
	private resizeFunc: any;

	private playingBeforeSuspend: boolean = false;

	private parallaxTween: gsap.core.Tween | null = null;
	private elementTweens: gsap.core.Tween[] = [];

	constructor() {
		super();

		this.resizeFunc = () => this.updateAutoCover('resize');
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const src = this.vars.get<string>(Attrs.src);
		if (!src || src.length === 0) {
			logger.log(LOG_PREFIX, 'src is required');
			return;
		}

		const type: HHDSHeroMediaType = this.vars.get<HHDSHeroMediaType>(Attrs.type);
		const mediaHtml: string = this.getMediaHtml(type);

		const overlayHtml = this.htmlIf(Attrs.overlayEnabled, () => {
			return `<div class="${TAG_NAME}__overlay"></div>`;
		});
		const scrollDownButtonHtml = this.htmlIf(Attrs.showScrollButton, () => {
			return `<hhds-button-hero class="${TAG_NAME}__button" mode="light"></hhds-button-hero>`;
		});
		const headerHtml = this.htmlIfVar(Attrs.header, (x) => {
			return `<h2 class="${TAG_NAME}__header heading heading--02">${x.toUpperCase()}</h2>`;
		});
		const subtitleHtml = this.htmlIfVar(Attrs.subtitle, (x) => {
			return `<p class="${TAG_NAME}__subtitle paragraph">${x}</p>`;
		});

		const html: string = `
      <div class="${TAG_NAME} ${this.getRatioClass()}">
				<div class="${TAG_NAME}__parallax-wrapper">
					<div class="${TAG_NAME}__parallax-content">
						${mediaHtml}
						${overlayHtml}
					</div>
				</div>
				<div class="${TAG_NAME}__content-wrapper">
					<div class="container">
						<div class="grid">
							<div class="col-span-4 md:col-span-6 md:col-start-4 lg:col-span-12 lg:col-start-7 ${TAG_NAME}__content">
								${headerHtml}
								${subtitleHtml}
								${scrollDownButtonHtml}
							</div>
						</div>
					</div>
				</div>
      </div>
			<div class="${TAG_NAME}__bottom-anchor"></div>
    `;

		this.setInnerHtml('', '', html);

		if (SUSPEND_PLAYBACK_FOR_DIALOG) {
			document.addEventListener(HHDSDialogEvent.show, () => {
				this.video?.isPlaying().then((isPlaying) => {
					this.playingBeforeSuspend = isPlaying;
					this.video?.pause();
				});
			});
			document.addEventListener(HHDSDialogEvent.hide, () => {
				this.playingBeforeSuspend && this.video?.play();
			});
		}

		this.image = this.root.querySelector(HHDSImageTagName) as HHDSImage;
		this.video = this.root.querySelector(HHDSVideoDirectTagName) as HHDSVideoDirect;
		this.overlay = this.root.querySelector(`.${TAG_NAME}__overlay`);
		this.overlayVisible = false;

		const videoInit: Promise<void> | null = this.awaitInitialisationOf(this.video);
		const imageInit: Promise<void> | null = this.awaitInitialisationOf(this.image);
		Promise.race([videoInit, imageInit])?.then(() => {
			requestAnimationFrame(() => {
				if (this.vars.get<boolean>(Attrs.parallax)) this.configureParallax();
			});
		});

		this.video?.addEventListener(HHDSVideoEvent.coverLoaded, () => {
			this.overlayVisible = true;
			this.updateAutoCover('cover loaded');
		});
		this.video?.addEventListener(HHDSVideoEvent.loaded, () => {
			this.overlayVisible = true;
			this.updateAutoCover('video loaded');
		});
		this.image?.addEventListener(HHDSImageEvent.load, () => {
			this.overlayVisible = true;
			this.updateAutoCover('image loaded');
		});

		const bottomAnchor = this.root.querySelector(`.${TAG_NAME}__bottom-anchor`) as HTMLElement;
		if (bottomAnchor) {
			const scrollButton = this.root.querySelector('hhds-button-hero');
			scrollButton?.addEventListener('click', () => {
				const rect = bottomAnchor.getBoundingClientRect();
				const yPos = rect.top + window.scrollY;
				window.scrollTo({ top: yPos, behavior: 'smooth' });
			});
		}

		window.addEventListener('resize', this.resizeFunc);

		document.addEventListener(PageEvent.enter, () => {
			if (!this.initialised) return; // Ignore if component is de-initialised by being on a leaving page
			this.prepareElementsForAnimation();
			this.animateElementsIn();
		});
		document.addEventListener(PageEvent.enterComplete, () => {
			if (!this.initialised) return; // Ignore if component is de-initialised by being on a leaving page
		});
		document.addEventListener(PageEvent.leave, () => {
			this.video?.pause();
			this.elementTweens.forEach((tween) => tween.kill());
			this.elementTweens = [];
		});
	}

	private getRatioClass(): string {
		const force16x9: boolean = this.vars.get<boolean>(Attrs.force16x9);
		return force16x9 ? `${TAG_NAME}__ratio-16x9` : ``;
	}

	private prepareElementsForAnimation(): void {
		const header = this.element(`.${TAG_NAME}__header`);
		const subtitle = this.element(`.${TAG_NAME}__subtitle`);
		const yOffset: number = 20;
		header && gsap.set(header, { y: yOffset, opacity: 0 });
		subtitle && gsap.set(subtitle, { y: yOffset, opacity: 0 });
	}

	private animateElementsIn(): void {
		const header = this.element(`.${TAG_NAME}__header`);
		const subtitle = this.element(`.${TAG_NAME}__subtitle`);
		const easing = Cubic.easeOut;
		let startTime: number = 0.1;
		const tweenDuration: number = 0.7;
		if (header) {
			this.elementTweens.push(gsap.to(header, { ease: easing, duration: tweenDuration, y: 0, opacity: 1, delay: startTime }));
			startTime += 0.1;
		}
		if (subtitle) {
			this.elementTweens.push(gsap.to(subtitle, { ease: easing, duration: tweenDuration, y: 0, opacity: 1, delay: startTime }));
		}
	}

	private configureParallax(): void {
		let animationTarget: HTMLElement;
		if (this.vars.get<HHDSHeroMediaType>(Attrs.type) === HHDSHeroMediaType.video) {
			// animationTarget = this.video?.videoElement as HTMLElement;
			// animationTarget = this.video as HTMLElement;
			animationTarget = this.querySelector('.hhds-hero__parallax-wrapper') as HTMLElement;
		} else {
			animationTarget = this.image as HTMLElement;
		}
		if (!animationTarget) {
			return;
		}

		// let distance = window.innerHeight * 0.2;
		// this.parallaxTween = gsap.to('.hhds-hero__parallax-content', {
		// 	y: distance,
		// 	ease: 'linear',
		// 	paused: true,
		// });

		// ScrollTrigger.create({
		// 	trigger: animationTarget,
		// 	start: 'top top',
		// 	end: 'bottom top',
		// 	scrub: true,
		// });

		let distance = window.innerHeight * 0.2;

		this.parallaxTween = gsap.to('.hhds-hero__parallax-content', {
			y: distance,
			ease: 'none',
			scrollTrigger: {
				trigger: animationTarget,
				start: 'top top',
				end: 'bottom top',
				scrub: true,
			},
		});
	}

	private set overlayVisible(visible: boolean) {
		if (this.overlay) {
			this.overlay.style.display = visible ? 'block' : 'none';
		}
	}

	private getMediaHtml(type: HHDSHeroMediaType): string {
		switch (type) {
			case HHDSHeroMediaType.video:
				let attributes = {
					[HHDSVideoDirectAttrNames.src]: this.vars.get<string>(Attrs.src),
					[HHDSVideoDirectAttrNames.cover]: this.vars.get<string>(Attrs.cover),
					[HHDSVideoDirectAttrNames.autoplay]: true,
					[HHDSVideoDirectAttrNames.loop]: true,
					[HHDSVideoDirectAttrNames.controls]: false,
				};
				let attributesString: string = '';
				Object.keys(attributes).forEach((key) => (attributesString += `${key}="${attributes[key]}" `));
				const style = `--video-component-width: auto;`;
				return `<${HHDSVideoDirectTagName} id="main" style="${style}" ${attributesString}></${HHDSVideoDirectTagName}>`;
			case HHDSHeroMediaType.image:
				const src = this.vars.get<string>(Attrs.src);
				const srcset = this.vars.get<string>(Attrs.srcSet);
				const sizes = this.vars.get<string>(Attrs.sizes);
				const alt = this.vars.get<string>(Attrs.alt);
				if (srcset?.length && sizes?.length) {
					return `<${HHDSImageTagName} srcset="${srcset}" alt="${alt}" sizes="${sizes}"></${HHDSImageTagName}>`;
				} else {
					return `<${HHDSImageTagName} alt="${alt}" src="${src}"></${HHDSImageTagName}>`;
				}
		}
	}

	private updateAutoCover(_reason: string): void {
		if (!this.initialised) return;

		const component = this.root.querySelector(`.${TAG_NAME}`) as HTMLElement;
		//logger.log(`${LOG_PREFIX} updateAutoCover: ${reason}`);
		if (!component) return;
		const parentRatio = component.clientWidth / component.clientHeight;
		switch (this.vars.get<HHDSHeroMediaType>(Attrs.type)) {
			case HHDSHeroMediaType.video:
				this.updateAutoCover_video(parentRatio);
				break;
			case HHDSHeroMediaType.image:
				this.updateAutoCover_image(parentRatio);
				break;
		}
	}

	private updateAutoCover_video(parentRatio: number): void {
		const videoDirect = this.root.querySelector(HHDSVideoDirectTagName) as HHDSVideoDirect;
		const videoElement = videoDirect.videoElement;
		let mediaRatio = 1920 / 1080;
		const coverElement = videoDirect.coverElement;

		videoDirect.componentElement.style.width = '100%';
		videoDirect.style.width = '100%';
		videoDirect.componentElement.style.height = '100%';
		videoDirect.style.height = '100%';

		if (videoElement?.videoWidth) {
			mediaRatio = videoElement.videoWidth / videoElement.videoHeight;
		} else if (coverElement?.clientWidth) {
			mediaRatio = coverElement.clientWidth / coverElement.clientHeight;
		}
		if (mediaRatio > parentRatio) {
			// Fit to height
			//logger.log(`${LOG_PREFIX} updateAutoCover: media ${mediaRatio.toFixed(2)}, parent ${parentRatio.toFixed(2)}, fit-to-height`);

			// Reset other styles used by fit-to-width
			if (videoElement) {
				videoElement.style.minWidth = 'unset';
				videoElement.style.minHeight = 'unset';
			}
			//videoDirect.componentElement.style.width = 'unset';
			//videoDirect.style.width = 'unset';

			// Apply fit-to-height styles
			//videoDirect.componentElement.style.height = '100%';
			//videoDirect.style.height = '100%';
			if (videoElement) {
				videoElement.style.maxHeight = '100%';
				videoElement.style.width = 'auto';
				videoElement.style.height = '100%';
			}
		} else {
			// Fit to width
			//logger.log(`${LOG_PREFIX} updateAutoCover: media ${mediaRatio.toFixed(2)}, parent ${parentRatio.toFixed(2)}, fit-to-width`);

			// Reset styles used by fit-to-height
			if (videoElement) {
				videoElement.style.maxHeight = 'unset';
			}
			//videoDirect.componentElement.style.height = 'unset';
			//videoDirect.style.height = 'unset';

			// Apply fit-to-width styles
			//videoDirect.componentElement.style.width = '100%';
			//videoDirect.style.width = '100%';
			if (videoElement) {
				videoElement.style.width = '100%';
				videoElement.style.height = 'auto';
				videoElement.style.minWidth = '100%';
				videoElement.style.minHeight = '100%';
			}
		}
	}

	private updateAutoCover_image(parentRatio: number): void {
		const image = this.root.querySelector(HHDSImageTagName) as HHDSImage;
		const mediaRatio = image.imageRatio;
		if (mediaRatio > parentRatio) {
			// Fit to height
			//logger.log(`${LOG_PREFIX} updateAutoCover (fit-to-height)`);
			image.style.height = '100%';
			image.style.setProperty('--image-component-height', '100%');
			image.style.setProperty('--image-height', '100%');
			// Reset styles used by fit-to-width
			image.style.width = 'unset';
		} else {
			// Fit to width
			//logger.log(`${LOG_PREFIX} updateAutoCover (fit-to-height)`);
			image.style.width = '100%';
			// Reset styles used by fit-to-height
			image.style.height = 'unset';
			image.style.setProperty('--image-component-height', 'unset');
			image.style.setProperty('--image-height', 'unset');
		}
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.video?.destroy();
		window.removeEventListener('resize', this.resizeFunc);
		this.elementTweens.forEach((tween) => tween.kill());
		this.elementTweens = [];
		this.parallaxTween?.kill();
		this.parallaxTween = null;
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: Element[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.type]: {
		description: 'Which type of media to display.',
		defaultValue: HHDSHeroMediaType.video,
		typeString: 'HHDSHeroMediaType',
		type: HHDSHeroMediaType,
	},
	[Attrs.src]: {
		description: 'URL to a direct MP4.',
		defaultValue: '',
		type: String,
	},
	[Attrs.cover]: {
		description: 'The URL of the cover image, if applicable.',
		defaultValue: '',
		type: String,
	},
	[Attrs.header]: {
		description: 'Header text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.subtitle]: {
		description: 'Subtitle text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.overlayEnabled]: {
		description: 'Whether to enable the gradient overlay.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.showScrollButton]: {
		description: 'Whether to show the scroll button.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.parallax]: {
		description: 'Whether to enable parallax scrolling.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.srcSet]: {
		description: 'The srcset attribute for the image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.sizes]: {
		description: 'The sizes attribute for the image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.alt]: {
		description: 'The alt tag for the image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.force16x9]: {
		description: 'Whether to force the media to be 16:9 aspect ratio.',
		defaultValue: false,
		type: Boolean,
	},
};
